import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";

import { useDispatch } from "react-redux";
import { useTranslation, withTranslation } from "react-i18next";

import PaginationContainer from "@plannel/components/PaginationContainer";
import Dialog from "@plannel/components/Dialog";
import ExcelExportButton from "@plannel/components/ExcelExportButton";
import Snackbar from "@plannel/components/Snackbar";
import DataState from "@plannel/components/aggrid/DataState";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import { AddButton, RemoveButton, SaveButton, SettingsButton, FilterButton } from "@plannel/components/ActionIconButton";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "../utils/color-util";
import AttributeConfigModal from "@plannel/components/modal/AttributeConfigModal";
import bfFeatureDateService from "@plannel/services/data/bf-feature-date-service";
import { isEmpty } from "lodash";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import GridDatePicker from "@plannel/components/GridDatePicker";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";

import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
const CURRENT_DATE = new ZDate();
const START_MIN_DATE = new ZDate(CURRENT_DATE.getFullYear() - 10, 0, 1);
const START_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth(), 1);
const END_DATE = new ZDate(CURRENT_DATE.getFullYear() + 1, CURRENT_DATE.getMonth(), 0);
const END_MAX_DATE = new ZDate(CURRENT_DATE.getFullYear() + 5, CURRENT_DATE.getMonth() + 1, 0);
const initPeriod = { start: START_DATE, end: END_DATE };

const { snackBarMessageColor } = ColorUtil;
const PeriodFilter = ({ period, setPeriod }) => {
  const { t } = useTranslation();

  const onChange = (e, type) => {
    if (type) {
      if (type === "start") {
        e.setDate(1);
        e.setHours(0);
        e.setMinutes(0);
        e.setSeconds(0);
      } else if (type === "end") {
        e.setMonth(e.getMonth() + 1);
        e.setDate(0);
        e.setHours(23);
        e.setMinutes(59);
        e.setSeconds(59);
      }

      setPeriod({ ...period, [type]: e });
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DatePicker
        label={t("periodStart")}
        views={["year", "month"]}
        format={"yyyy-MM"}
        value={period.start}
        maxDate={period.end ? period.end : END_MAX_DATE}
        minDate={START_MIN_DATE}
        onAccept={onChange}
        onMonthChange={e => onChange(e, "start")}
        onYearChange={e => onChange(e, "start")}
        onClose={onChange}
        slotProps={{ textField: { InputProps: { color: "primary", size: "small", sx: { mr: 1.5, width: 200 } } } }}
      />
      <DatePicker
        label={t("periodEnd")}
        views={["year", "month"]}
        format={"yyyy-MM"}
        value={period.end}
        maxDate={END_MAX_DATE}
        minDate={period.start ? period.start : START_MIN_DATE}
        onAccept={onChange}
        onMonthChange={e => onChange(e, "end")}
        onYearChange={e => onChange(e, "end")}
        onClose={onChange}
        slotProps={{ textField: { InputProps: { color: "primary", size: "small", sx: { mr: 1.5, width: 200 } } } }}
      />
    </LocalizationProvider>
  );
};

const BfFeatureDate = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const pageSizes = [100, 200, 500];
  const [resultTotalCount, setResultTotalCount] = useState(null);
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ?? 1);
  const [tPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);
  const [sortParams, setSortParams] = useState({});

  const [period, setPeriod] = useState(reduxViewState?.start && reduxViewState?.end ? { start: reduxViewState.start, end: reduxViewState.end } : initPeriod);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [attrConfigModalOpen, setAttrConfigModalOpen] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);
  const [featureColDefs, setFeatureColDefs] = useState([]);

  const [bfDatas, setBfDatas] = useState([]);
  // const attrColsCollapsed = reduxViewState && Object.prototype.hasOwnProperty.call(reduxViewState, "attrColsCollapsed") ? reduxViewState.attrColsCollapsed : true;

  const featureField = () => {
    const fields = [];
    for (let idx = 0; idx < 20; idx++) {
      fields.push(`feature${String(idx + 1).padStart(2, "0")}`);
    }
    return fields;
  };

  const getRequestParams = () => {
    const params = {};
    const searchFilters = {};

    if (period?.start && period?.end) {
      searchFilters.startDate = period.start;
      searchFilters.endDate = period.end;
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    params.searchFilters = searchFilters;

    return { ...params, ...sortParams };
  };

  const retrieveBFFeatureDates = () => {
    const params = getRequestParams();
    bfFeatureDateService.get(params)
      .then(response => {
        const { results, totalPages, totalItems } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
        setBfDatas(results);
        setTotalPages(totalPages);
        setResultTotalCount(totalItems)
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { currentPage, pageSize, ...period, advancedFilters });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
      });
  };

  const refreshGrid = () => {
    retrieveBFFeatureDates();
  };

  const columnDefs = useMemo(() => {
    featureColDefs.forEach((colDef) => {
      if (!colDef.hide) {
        colDef.filterType = "number";
        colDef.filterName = colDef.field;
      }
    });

    return [
      {
        headerName: "date",
        field: "baseDate",
        type: ["defaultValueParser"],
        cellEditor: GridDatePicker,
        cellEditorParams: { dateType: "day", popupGrid: true },
        width: 200, filterType: "date", valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd")
      },
      ...featureColDefs,
      { headerName: "createdTs", field: "createdTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, filterType: 'timestamp' },
      { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150, filterType: 'string' },
      { headerName: "updatedTs", field: "updatedTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, filterType: 'timestamp' },
      { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: 'string' }
    ];
  }, [featureColDefs]);

  const insertRow = () => {
    const newRow = {
      id: null,
      baseDate: null,
      feature01: null,
      feature02: null,
      feature03: null,
      feature04: null,
      feature05: null,
      feature06: null,
      feature07: null,
      feature08: null,
      feature09: null,
      feature10: null,
      feature11: null,
      feature12: null,
      feature13: null,
      feature14: null,
      feature15: null,
      feature16: null,
      feature17: null,
      feature18: null,
      feature19: null,
      feature20: null,
      verNum: null,
    };

    const addRow = gridRef.current.api.applyTransaction({ add: [newRow], addIndex: 0 }).add[0];

    DataState.setDataState(addRow);
  };

  const removeBfFeatureDate = () => {
    const bfFeatureDateIds = [];

    gridRef.current.api.getSelectedRows().forEach(data => {
      bfFeatureDateIds.push(data.id);
    });

    if (bfFeatureDateIds.length > 0) {
      bfFeatureDateService.remove(bfFeatureDateIds)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
        })
        .then(() => {
          refreshGrid();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const saveBfFeatureDate = () => {
    const allStateData = DataState.getAllStateData(gridRef.current.api);

    if (allStateData.length > 0) {
      bfFeatureDateService.upsert(allStateData)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .catch(e => {
          const { message } = e.response?.data;
          if (message) {
            setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          }
          debug(e);
        })
        .then(() => {
          refreshGrid();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const handleDialog = action => {
    if (action === "SAVE") {
      saveBfFeatureDate();
    } else if (action === "DELETE") {
      removeBfFeatureDate();
    }
  };

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveBFFeatureDates();
    }
  };

  const onGridReady = params => {
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  };

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  useEffect(() => {
    if (gridLoading) {
      DataState.initialize(gridRef.current?.api);
      retrieveBFFeatureDates();
    }

    return () => {
      setBfDatas([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    return () => {
      setBfDatas([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters, period]);

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  return (
    <>
      <FilterContainer>
        <PeriodFilter
          period={period}
          setPeriod={setPeriod}
        />
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton
            title={title}
            service={bfFeatureDateService}
            columnDefs={columnDefs}
            totalRows={resultTotalCount}
            params={getRequestParams()}
          />
          <AddButton onClick={insertRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
          <SettingsButton
            label="settings"
            onClick={() => setAttrConfigModalOpen(true)}
          />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box className="ag-theme-balham grid-area" mb={1}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={bfDatas}
              {...DefaultGridSetting({ title, viewName })}
              rowSelection={{
                ...DefaultGridSetting({ title, viewName }).rowSelection,
                checkboxes: true,
                headerCheckbox: true,
              }}
              columnDefs={GridUtils.getColumnDefs(columnDefs)}
              onGridReady={onGridReady}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            />
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <PaginationContainer
        currentPage={currentPage}
        totalPages={tPages}
        pageSize={pageSize}
        pageSizes={pageSizes}
        setCurrentPage={setCurrentPage}
        setPageSize={setPageSize}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
        disableButton={dialogInfo.disableButton}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <AttributeConfigModal
        viewName={viewName}
        open={attrConfigModalOpen}
        setOpen={setAttrConfigModalOpen}
        target={"date"}
        attrFields={featureField()}
        setAttrColDefs={setFeatureColDefs}
        attrColumnOptions={{ valueParser: params => String(params.newValue) }}
        // attrColsCollapsed={attrColsCollapsed}
        attrColumnGroupOptions={{ type: ["rightAligned"] }}
      />
    </>
  );
};

export default withTranslation()(BfFeatureDate);
